/**
 * RejectEventDialog — modal de rejet d'un intake_event avec raison textuelle
 * (Chunk 9.G.2 frontend, parité pattern RevokeIntakeLinkDialog).
 *
 * Phase 1 décisions PO lockées :
 *   - Q2=A 2 dialogs séparés (ResolveDialog + RejectDialog).
 *   - Q5=B race 409 INVALID_TRANSITION : auto-close + parent refetch + toast info.
 *   - Q7=A pas de double confirm. Textarea obligatoire (1..500 chars) = frein
 *     suffisant. Focus initial sur Cancel (anti-clic-réflexe destructive).
 *   - Bouton Reject = red (action destructive terminale).
 *
 * Pattern dialog parité [RevokeIntakeLinkDialog] + textarea avec compteur live.
 *
 * Schema backend : `RejectIntakeEventSchema` exige `rejection_reason: string.min(1).max(500)`.
 * Le bouton Submit est disabled si <1 ou >500 chars.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useRejectIntakeEvent } from "../../../lib/queries/intakeLinks";
import { toast } from "../../../lib/toast";

const MIN_REASON_LEN = 1;
const MAX_REASON_LEN = 500;

type Props = {
  readonly open: boolean;
  readonly eventId: string | null;
  /**
   * UUID intake_link parent — `null` pour les intake_events orphans (9.T.3
   * broker upload : `intake_link_id=NULL`). Parité [[ResolveEventDialog]].
   */
  readonly linkId: string | null;
  readonly onClose: () => void;
  readonly onRejected: () => void;
};

export function RejectEventDialog({ open, eventId, linkId, onClose, onRejected }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const reasonFieldId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();

  const reject = useRejectIntakeEvent();
  const { reset: resetMutation } = reject;

  // État local — rejection_reason textarea.
  const [reason, setReason] = useState("");

  // Reset à chaque ouverture + focus initial Cancel.
  useEffect(() => {
    if (open) {
      resetMutation();
      setReason("");
      queueMicrotask(() => cancelBtnRef.current?.focus());
    }
  }, [open, resetMutation]);

  // Esc → close (sauf pendant submit).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !reject.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, reject.isPending]);

  if (!open || !eventId) return null;

  const trimmedReason = reason.trim();
  const reasonLen = trimmedReason.length;
  const isReasonValid = reasonLen >= MIN_REASON_LEN && reasonLen <= MAX_REASON_LEN;

  const onConfirm = () => {
    if (!isReasonValid) return;
    reject.mutate(
      {
        eventId,
        linkId,
        input: { rejection_reason: trimmedReason },
      },
      {
        onSuccess: () => onRejected(),
        onError: (err) => {
          if (err instanceof ApiException && err.status === 409) {
            void queryClient.invalidateQueries({ queryKey: ["intake-events", "by-link"] });
            void queryClient.invalidateQueries({ queryKey: ["intake-events", "list"] });
            if (linkId) {
              void queryClient.invalidateQueries({
                queryKey: ["intake-links", "detail", linkId],
              });
            }
            void queryClient.invalidateQueries({
              queryKey: ["intake-events", "detail", eventId],
            });
            toast.info(t("intake_links.detail.events.actions.race_lost"));
            onClose();
          }
        },
      },
    );
  };

  const apiErrorMsg =
    reject.error instanceof ApiException && reject.error.status !== 409
      ? getApiErrorMessage(reject.error, lang) || t("errors.generic")
      : reject.isError && !(reject.error instanceof ApiException && reject.error.status === 409)
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!reject.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("intake_links.detail.events.actions.reject.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {t("intake_links.detail.events.actions.reject.body")}
          </p>

          <div>
            <label
              htmlFor={reasonFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("intake_links.detail.events.actions.reject.reason_label")}{" "}
              <span aria-hidden="true" className="text-red-600">
                *
              </span>
            </label>
            <textarea
              id={reasonFieldId}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={reject.isPending}
              maxLength={MAX_REASON_LEN}
              required
              rows={4}
              className="input min-h-[6rem] resize-y"
              placeholder={t("intake_links.detail.events.actions.reject.reason_placeholder")}
            />
            <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
              <span>{t("intake_links.detail.events.actions.reject.reason_hint")}</span>
              <span aria-live="polite" className={reasonLen > MAX_REASON_LEN ? "text-red-600" : ""}>
                {reasonLen} / {MAX_REASON_LEN}
              </span>
            </div>
          </div>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={reject.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={reject.isPending || !isReasonValid}
            aria-busy={reject.isPending}
            className="inline-flex touch-manipulation items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {reject.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {reject.isPending
              ? t("intake_links.detail.events.actions.reject.submitting")
              : t("intake_links.detail.events.actions.reject.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
